import { useCallback } from 'react'
import { ProductAvailabilityInfo } from '@leid/api-orders'
import { addHours, startOfHour } from 'date-fns'

import { buildDate, utcNow } from 'app/utils/dates'
import { CartFormValues } from 'appTypes/orders'
import { CheckIfRerentPredicate } from 'components/form/DateTimePicker/interfaces'
import { getProductAvailabilitiesInfoForPeriod } from 'helpers/utils/availabilities'

export const useCheckIfRerent = (
  values: CartFormValues,
  productCalendarList: ProductAvailabilityInfo,
): CheckIfRerentPredicate => {
  return useCallback(
    (locationId: number | undefined): boolean => {
      if (!locationId) {
        // If no location is selected - not re-rent
        return false
      }

      if (!values.startDate || !values.pickupAt) {
        const date = startOfHour(addHours(utcNow(), 1))
        // No start date yet selected - not re-rent
        const periodsInfo = getProductAvailabilitiesInfoForPeriod(locationId, date, undefined, productCalendarList)
        return periodsInfo.length !== 0 && periodsInfo.every((s) => s.isRerent === true)
      }

      if (!values.endDate || !values.returnAt) {
        // No pickup date yet selected - not re-rent
        const fullStartDate = buildDate(values.startDate, values.pickupAt)
        const periodsInfo = getProductAvailabilitiesInfoForPeriod(
          locationId,
          fullStartDate,
          undefined,
          productCalendarList,
        )
        return periodsInfo.length !== 0 && periodsInfo.every((s) => s.isRerent === true)
      }

      const fullStartDate = buildDate(values.startDate, values.pickupAt)
      const fullEndDate = buildDate(values.endDate, values.returnAt)

      const periodsInfo = getProductAvailabilitiesInfoForPeriod(
        locationId,
        fullStartDate,
        fullEndDate,
        productCalendarList,
      )
      // Return if any availability period is a rerent period
      return periodsInfo.some((s) => s.isRerent === true)
    },
    [values.startDate, values.pickupAt, values.endDate, values.returnAt, productCalendarList],
  )
}
